import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import HRHeader from "../components/HRHeader";
import api from "../api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { LogIn, LogOut, Clock, Fingerprint, Users, CheckCircle2, Coffee, Play, AlertTriangle, Zap, Check, X, Hourglass, ChevronDown } from "lucide-react";
import InteractiveTour from "../components/InteractiveTour";
import { GuidaRapidaButton } from "../components/GuidaRapidaButton";
import { usePageTour } from "../hooks/usePageTour";
import { presenzeTour } from "../miniTours";
import { SHIFT_LABELS, SHIFT_COLORS } from "../shifts";

const timeOf = (iso) => { try { return new Intl.DateTimeFormat("it-IT", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/Rome" }).format(new Date(iso)); } catch { return "--:--"; } };
const dateOf = (iso) => { try { return format(new Date(iso), "EEE dd MMM", { locale: it }); } catch { return iso; } };
const fmtHM = (min) => {
  if (min == null) return "--";
  const m = Math.max(0, Math.round(min));
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${h}h${r > 0 ? ` ${r}m` : ""}`;
};
const pauseMinOf = (pauses) => {
  let t = 0; const now = Date.now();
  (pauses || []).forEach((p) => {
    if (p.start) { const s = new Date(p.start).getTime(); const e = p.end ? new Date(p.end).getTime() : now; t += Math.max(0, (e - s) / 60000); }
  });
  return t;
};

const STATUS_BADGE = {
  presente: { cls: "bg-green-100 text-green-700 border-green-200", label: "Presente (P)", icon: CheckCircle2 },
  permesso: { cls: "bg-amber-100 text-amber-800 border-amber-300", label: "Permesso", icon: AlertTriangle },
  straordinario: { cls: "bg-indigo-100 text-indigo-700 border-indigo-200", label: "Straordinario", icon: Zap },
};

const OT_STATUS = {
  in_attesa: { cls: "bg-amber-100 text-amber-800", label: "In attesa" },
  approvata: { cls: "bg-green-100 text-green-700", label: "Approvata" },
  rifiutata: { cls: "bg-red-100 text-red-700", label: "Rifiutata" },
};

const OT_KIND_LABEL = { feriale: "Feriale", sabato: "Sabato", festivo: "Domenica/Festivo", notturno: "Notturno" };
const otKindLabel = (kind) => (kind ? kind.split("_").map((p) => OT_KIND_LABEL[p] || p).join(" + ") : "");

const PresenzePage = () => {
  const { isApprover } = useAuth();
  const tour = usePageTour("presenze");
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [team, setTeam] = useState([]);
  const [otPending, setOtPending] = useState([]);
  const [shiftsToday, setShiftsToday] = useState([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = () => {
    api.get("/attendance/today").then((r) => setToday(r.data)).catch(() => {});
    api.get("/attendance/mine").then((r) => setHistory(r.data)).catch(() => {});
    const d = format(new Date(), "yyyy-MM-dd");
    api.get(`/shifts/calendar?start=${d}&end=${d}`).then((r) => setShiftsToday(r.data || [])).catch(() => {});
    if (isApprover) {
      api.get("/attendance/team").then((r) => setTeam(r.data)).catch(() => {});
      api.get("/attendance/overtime/pending").then((r) => setOtPending(r.data)).catch(() => {});
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const act = async (path, okMsg) => {
    setBusy(true);
    try { await api.post(`/attendance/${path}`); toast.success(okMsg); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const requestOvertime = async () => {
    setBusy(true);
    try { await api.post("/attendance/overtime/request", { note: "" }); toast.success("Richiesta di straordinario inviata al responsabile"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const decideOvertime = async (id, decision) => {
    setBusy(true);
    try { await api.post(`/attendance/overtime/${id}/decision`, { decision }); toast.success(decision === "approvata" ? "Straordinario approvato" : "Straordinario rifiutato"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const hasIn = today?.check_in;
  const hasOut = today?.check_out;
  const pauses = today?.pauses || [];
  const openPause = pauses.length > 0 && !pauses[pauses.length - 1].end;
  const todayPauseMin = pauseMinOf(pauses);
  const badge = hasOut && today?.status ? STATUS_BADGE[today.status] : null;

  return (
    <div className="min-h-screen bg-[#f0f2f5]">
      <HRHeader />
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><Fingerprint size={24} /> Presenze</h1>
            <p className="text-[14px] text-gray-500">Timbra entrata, pause e uscita. La prestazione netta di riferimento è 8 ore (tolleranza 7,5 minuti).</p>
          </div>
          <GuidaRapidaButton onClick={tour.open} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-[#16345c] to-[#0d1b2f] text-white rounded-xl p-6 text-center" data-testid="timbratura-card">
              <div className="text-[13px] text-gray-300 uppercase tracking-wide mb-1">Oggi</div>
              <div className="text-[15px] font-semibold mb-4 capitalize">{format(new Date(), "EEEE dd MMMM yyyy", { locale: it })}</div>
              <div className="flex items-center justify-center gap-5 mb-4">
                <div>
                  <div className="text-[11px] text-gray-300">Entrata</div>
                  <div className="text-[22px] font-black" data-testid="today-checkin">{hasIn ? timeOf(today.check_in) : "--:--"}</div>
                </div>
                <div className="w-px h-10 bg-white/20" />
                <div>
                  <div className="text-[11px] text-gray-300">Pausa</div>
                  <div className="text-[22px] font-black" data-testid="today-pause">{hasIn ? fmtHM(todayPauseMin) : "--"}</div>
                </div>
                <div className="w-px h-10 bg-white/20" />
                <div>
                  <div className="text-[11px] text-gray-300">Uscita</div>
                  <div className="text-[22px] font-black" data-testid="today-checkout">{hasOut ? timeOf(today.check_out) : "--:--"}</div>
                </div>
              </div>

              {!hasIn && (
                <button onClick={() => act("checkin", "Entrata registrata")} disabled={busy} data-testid="btn-checkin" className="w-full flex items-center justify-center gap-2 bg-[#009640] hover:bg-[#007a34] text-white font-bold text-[15px] py-3 rounded-lg transition-colors disabled:opacity-60">
                  <LogIn size={18} /> Timbra Entrata
                </button>
              )}

              {hasIn && !hasOut && (
                <div className="space-y-2">
                  {openPause ? (
                    <button onClick={() => act("pause/end", "Pausa terminata")} disabled={busy} data-testid="btn-pause-end" className="w-full flex items-center justify-center gap-2 bg-[#f0ad4e] hover:bg-[#e09b3d] text-white font-bold text-[14px] py-2.5 rounded-lg transition-colors disabled:opacity-60">
                      <Play size={17} /> Fine Pausa
                    </button>
                  ) : (
                    <button onClick={() => act("pause/start", "Pausa iniziata")} disabled={busy} data-testid="btn-pause-start" className="w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold text-[14px] py-2.5 rounded-lg transition-colors disabled:opacity-60">
                      <Coffee size={17} /> Inizio Pausa
                    </button>
                  )}
                  <button onClick={() => act("checkout", "Uscita registrata")} disabled={busy || openPause} data-testid="btn-checkout" className="w-full flex items-center justify-center gap-2 bg-[#e30613] hover:bg-[#c00510] text-white font-bold text-[15px] py-3 rounded-lg transition-colors disabled:opacity-60">
                    <LogOut size={18} /> Timbra Uscita
                  </button>
                  {openPause && <div className="text-[11px] text-amber-200">Termina la pausa in corso per poter timbrare l'uscita.</div>}
                </div>
              )}

              {hasOut && badge && (
                <div className="space-y-2" data-testid="day-summary">
                  <div className="flex items-center justify-center gap-2 bg-white/10 text-white font-bold text-[14px] py-2.5 rounded-lg">
                    <Clock size={16} /> Prestazione netta: {fmtHM(today.net_minutes)}
                  </div>
                  <div className={`flex items-center justify-center gap-2 border font-bold text-[13.5px] py-2.5 rounded-lg ${badge.cls}`} data-testid="day-status-badge">
                    <badge.icon size={16} />
                    {badge.label}
                    {today.status === "permesso" && ` di ${fmtHM(today.permesso_minutes)}`}
                    {today.status === "straordinario" && ` di ${fmtHM(today.straordinario_minutes)}${today.straordinario_kind ? ` · ${otKindLabel(today.straordinario_kind)}` : ""}${today.straordinario_pct != null ? ` (+${today.straordinario_pct}%)` : ""}`}
                  </div>
                  {today.status === "permesso" && <div className="text-[11px] text-amber-200">Prestazione inferiore a 8h: permesso da evidenziare (non scala i saldi).</div>}
                  {today.status === "straordinario" && today.is_rest_day && <div className="text-[11px] text-indigo-200" data-testid="weekend-overtime-note">Giorno di riposo/festivo: tutte le ore lavorate sono straordinario ({otKindLabel(today.straordinario_kind)}).</div>}
                </div>
              )}
            </div>

            {/* Turni di oggi */}
            <div className="bg-white border border-gray-200 rounded-lg mt-5 overflow-hidden" data-testid="shifts-today-card">
              <div className="px-4 py-2.5 bg-[#7c3aed] text-white text-[13px] font-bold uppercase tracking-wide flex items-center gap-2"><Clock size={15} /> Turni di oggi</div>
              {shiftsToday.length === 0 ? (
                <div className="p-5 text-center text-gray-400 text-[13px]">Nessun turno oggi</div>
              ) : shiftsToday.map((s) => (
                <div key={s.id} className="px-4 py-2.5 border-b border-gray-100 last:border-0 flex items-center justify-between gap-2" data-testid={`shift-today-${s.id}`}>
                  <span className="text-[13px] font-semibold text-[#16345c] truncate">{s.employee_name}</span>
                  <span className="text-[12px] font-bold px-2 py-0.5 rounded-full shrink-0" style={{ backgroundColor: `${SHIFT_COLORS[s.type]}1a`, color: SHIFT_COLORS[s.type] }}>{SHIFT_LABELS[s.type]} · {s.start}–{s.end}</span>
                </div>
              ))}
            </div>

            {/* Straordinario self-service */}
            {hasIn && (
              <div className="bg-white border border-gray-200 rounded-lg mt-5 p-4" data-testid="overtime-box">
                <h3 className="text-[13px] font-bold text-[#16345c] flex items-center gap-2 mb-2"><Zap size={15} className="text-indigo-600" /> Straordinario di oggi</h3>
                {today?.overtime_authorized ? (
                  <div className="flex items-center gap-2 text-[13px] text-green-700 bg-green-50 border border-green-200 rounded-sm px-3 py-2" data-testid="overtime-mine-status"><CheckCircle2 size={15} /> Straordinario autorizzato dal responsabile</div>
                ) : today?.overtime_status ? (
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-gray-600">Richiesta straordinario</span>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${OT_STATUS[today.overtime_status]?.cls}`} data-testid="overtime-mine-status">{OT_STATUS[today.overtime_status]?.label}</span>
                  </div>
                ) : (
                  <>
                    <p className="text-[12px] text-gray-500 mb-2">Le ore oltre le 8h — o <b>qualsiasi ora lavorata di sabato/domenica</b> — sono conteggiate come straordinario solo se autorizzate in anticipo dal responsabile.</p>
                    <button onClick={requestOvertime} disabled={busy} data-testid="btn-request-overtime" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] font-bold py-2.5 rounded-sm transition-colors disabled:opacity-60">Richiedi autorizzazione straordinario</button>
                  </>
                )}
              </div>
            )}

            {isApprover && (
              <div className="bg-white border border-gray-200 rounded-lg mt-5 overflow-hidden">
                <div className="px-4 py-2.5 bg-[#16345c] text-white text-[13px] font-bold uppercase tracking-wide flex items-center gap-2"><Users size={15} /> Team presente oggi</div>
                {team.length === 0 ? (
                  <div className="p-5 text-center text-gray-400 text-[13px]">Nessuna timbratura</div>
                ) : team.map((t) => (
                  <div key={t.id} className="px-4 py-2.5 border-b border-gray-100 last:border-0 flex items-center justify-between">
                    <span className="text-[13px] font-semibold text-[#16345c]">{t.employee_name}</span>
                    <span className="text-[12px] text-gray-500 flex items-center gap-1">
                      <span className={`w-2 h-2 rounded-full ${t.check_out ? "bg-gray-400" : "bg-[#009640]"}`} />
                      {timeOf(t.check_in)}{t.check_out ? ` - ${timeOf(t.check_out)}` : " (in sede)"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            {/* Approvazioni straordinario (responsabile/HR) */}
            {isApprover && (
              <div className="bg-white border border-gray-200 rounded-lg mb-6 overflow-hidden" data-testid="overtime-approvals">
                <div className="px-5 py-3 bg-indigo-600 text-white text-[13px] font-bold uppercase tracking-wide flex items-center gap-2"><Zap size={15} /> Richieste straordinario da autorizzare</div>
                {otPending.length === 0 ? (
                  <div className="p-6 text-center text-gray-400 text-[13px]">Nessuna richiesta in attesa</div>
                ) : otPending.map((o) => (
                  <div key={o.id} className="px-5 py-3 border-b border-gray-100 last:border-0 flex items-center justify-between gap-3" data-testid={`overtime-pending-${o.id}`}>
                    <div>
                      <div className="text-[14px] font-semibold text-[#16345c]">{o.employee_name}</div>
                      <div className="text-[12px] text-gray-500">{dateOf(o.date)}{o.note ? ` · ${o.note}` : ""}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => decideOvertime(o.id, "approvata")} disabled={busy} data-testid={`overtime-approve-${o.id}`} title="Approva" className="flex items-center gap-1 bg-[#009640] hover:bg-[#007a34] text-white text-[12px] font-bold px-3 py-1.5 rounded-sm transition-colors disabled:opacity-60"><Check size={14} /> Approva</button>
                      <button onClick={() => decideOvertime(o.id, "rifiutata")} disabled={busy} data-testid={`overtime-reject-${o.id}`} title="Rifiuta" className="flex items-center gap-1 bg-white border border-[#e30613] text-[#e30613] hover:bg-red-50 text-[12px] font-bold px-3 py-1.5 rounded-sm transition-colors disabled:opacity-60"><X size={14} /> Rifiuta</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <h2 className="text-[16px] font-bold text-[#16345c] mb-4 flex items-center gap-2"><Clock size={18} /> Storico timbrature</h2>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 px-5 py-3 bg-[#16345c] text-white text-[11px] font-bold uppercase tracking-wide">
                <div className="col-span-3">Giorno</div>
                <div className="col-span-2 text-center">Entrata</div>
                <div className="col-span-2 text-center">Uscita</div>
                <div className="col-span-2 text-center">Pausa</div>
                <div className="col-span-3 text-center">Netto / Stato</div>
              </div>
              {history.length === 0 ? (
                <div className="p-10 text-center text-gray-400">Nessuna timbratura registrata</div>
              ) : history.map((h) => {
                const b = h.check_out && h.status ? STATUS_BADGE[h.status] : null;
                const hPauses = h.pauses || [];
                const isOpen = !!expanded[h.date];
                return (
                  <React.Fragment key={h.id}>
                  <div className="grid grid-cols-12 items-center px-5 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors text-[13px]" data-testid={`history-row-${h.date}`}>
                    <div className="col-span-3 font-semibold text-[#16345c] capitalize flex items-center gap-1">
                      {hPauses.length > 0 && (
                        <button onClick={() => setExpanded((e) => ({ ...e, [h.date]: !e[h.date] }))} data-testid={`history-expand-${h.date}`} title="Dettaglio pause" className="text-gray-400 hover:text-[#16345c]">
                          <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        </button>
                      )}
                      {dateOf(h.date)}
                    </div>
                    <div className="col-span-2 text-center text-gray-700">{timeOf(h.check_in)}</div>
                    <div className="col-span-2 text-center text-gray-700">{h.check_out ? timeOf(h.check_out) : <span className="text-[#009640] font-semibold">In corso</span>}</div>
                    <div className="col-span-2 text-center text-gray-500">{h.check_out ? fmtHM(h.pause_minutes || 0) : "--"}{hPauses.length > 1 ? ` (${hPauses.length})` : ""}</div>
                    <div className="col-span-3 text-center">
                      {h.check_out ? (
                        <div className="flex flex-col items-center gap-1">
                          <span className="font-bold text-[#16345c]">{fmtHM(h.net_minutes != null ? h.net_minutes : (h.hours != null ? h.hours * 60 : null))}</span>
                          {b && (
                            <span className={`inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-full border ${b.cls}`}>
                              {h.status === "presente" ? "P" : h.status === "permesso" ? `Permesso ${fmtHM(h.permesso_minutes)}` : `Straord. ${fmtHM(h.straordinario_minutes)}${h.straordinario_pct != null ? ` +${h.straordinario_pct}%` : ""}`}
                            </span>
                          )}
                        </div>
                      ) : <Hourglass size={15} className="mx-auto text-gray-300" />}
                    </div>
                  </div>
                  {isOpen && hPauses.length > 0 && (
                    <div className="px-5 py-2 bg-gray-50 border-b border-gray-100" data-testid={`history-pauses-${h.date}`}>
                      <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1"><Coffee size={12} /> Dettaglio pause</div>
                      <ul className="space-y-0.5">
                        {hPauses.map((p, i) => {
                          const dur = p.start && p.end ? (new Date(p.end) - new Date(p.start)) / 60000 : null;
                          return (
                            <li key={i} className="text-[12.5px] text-gray-600 flex items-center gap-2">
                              <span className="font-mono">{timeOf(p.start)} → {p.end ? timeOf(p.end) : <span className="text-[#f0ad4e]">in corso</span>}</span>
                              {dur != null && <span className="text-gray-400">({fmtHM(dur)})</span>}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </main>
      {tour.show && <InteractiveTour steps={presenzeTour} requireAck={false} onClose={tour.close} />}
    </div>
  );
};

export default PresenzePage;
